/**
 * RecentCartListPage.js - 最近更新的购物车列表
 * 展示用户最近更新的购物车，包含产品、用户名和账号
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Typography,
  Input,
  Tag,
  Space,
  Button,
  message,
} from 'antd';
import { ReloadOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { adminCartApi } from '../../api/index';

const { Title, Text } = Typography;
const { Search } = Input;

export default function RecentCartListPage() {
  const [carts, setCarts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [keyword, setKeyword] = useState('');

  const fetchCarts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminCartApi.listRecent({ page, pageSize, keyword });
      if (Array.isArray(data)) {
        setCarts(data);
        setTotal(data.length);
      } else {
        setCarts(data?.items || data?.list || []);
        setTotal(data?.total || 0);
      }
    } catch (err) {
      message.error('加载购物车列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword]);

  useEffect(() => {
    fetchCarts();
  }, [fetchCarts]);

  const columns = [
    {
      title: '账号(邮箱)',
      dataIndex: 'userEmail',
      key: 'userEmail',
      width: 220,
      render: (email, record) =>
        email ? (
          <Text copyable={{ text: email }}>{email}</Text>
        ) : (
          <Tag color="default">游客 #{record.sessionId?.slice(0, 8) || '-'}</Tag>
        ),
    },
    {
      title: '用户名',
      dataIndex: 'userName',
      key: 'userName',
      width: 160,
      render: (name) => name || <Text type="secondary">-</Text>,
    },
    {
      title: '产品',
      dataIndex: 'items',
      key: 'items',
      render: (items) => {
        if (!items || items.length === 0) {
          return <Text type="secondary">空购物车</Text>;
        }
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {items.map((it) => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {it.imageUrl && (
                  <img
                    src={it.imageUrl}
                    alt={it.productTitle}
                    style={{
                      width: 32,
                      height: 32,
                      objectFit: 'cover',
                      borderRadius: 4,
                      border: '1px solid #f0f0f0',
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: 13 }}>{it.productTitle}</Text>
                  {it.variantTitle && it.variantTitle !== 'Default Title' && (
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                      ({it.variantTitle})
                    </Text>
                  )}
                  <Tag style={{ marginLeft: 8 }} color="blue">
                    x{it.quantity}
                  </Tag>
                </div>
              </div>
            ))}
          </Space>
        );
      },
    },
    {
      title: '商品数',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 80,
      align: 'center',
      render: (n) => <Tag color="processing">{n || 0}</Tag>,
    },
    {
      title: '小计',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 100,
      align: 'right',
      render: (v) => `¥${(v || 0).toFixed(2)}`,
    },
    {
      title: '最后更新',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (t) => (t ? new Date(t).toLocaleString() : '-'),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          <ShoppingCartOutlined style={{ marginRight: 8 }} />
          最近更新的购物车
        </Title>
        <Space>
          <Search
            placeholder="搜索邮箱或用户名"
            style={{ width: 280 }}
            onSearch={(v) => {
              setPage(1);
              setKeyword(v);
            }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={fetchCarts}>
            刷新
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={carts}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
          }}
          locale={{ emptyText: '暂无购物车数据' }}
        />
      </Card>
    </div>
  );
}
